import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useToast} from "../../modules/layout/components/ToastContext";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import Loader from "../../modules/layout/components/Loader";
import {getById} from "../_request";
import {IConsumptionLog} from "../../interfaces/IUtilities";
import {formatDateTime} from "../../helpers/DateTimeHelper";

const ConsumptionLogDetails = () => {
    const {t} = useTranslation();
    const {showToast} = useToast();
    const navigate = useNavigate();
    const {id} = useParams<{id: string}>();

    const [log, setLog] = useState<IConsumptionLog | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            try {
                const result = await getById('/api/consumptionlogs/', id || '');
                const respStatus = result?.status || result?.response?.status;
                if (respStatus === 200) {
                    setLog(result?.data as IConsumptionLog);
                } else if (respStatus === 404) {
                    navigate("/consumption-logs/list");
                } else if (respStatus === 403) {
                    navigate("/error/403");
                } else if (respStatus === 401) {
                    navigate("/auth/login");
                } else {
                    showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                }
            } catch (error) {
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            } finally {
                setLoading(false);
            }
        };
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) return <Loader/>;
    if (!log) return null;

    const fmt = (d?: string) => d ? formatDateTime(d) : '—';

    return (
        <div className="gg-consumptionLogDetails container mt-4">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('ConsumptionLogList'), href: "/consumption-logs/list"},
                    {title: t('ConsumptionLogDetails')}
                ]}/>

            <h3 className="mb-3">{t('ConsumptionLogDetails')}</h3>

            <div className="card">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6"><p><strong>{t('SpokeUserId')}:</strong> {log.spokeUserId}</p></div>
                        <div className="col-md-6"><p><strong>{t('SpokeName')}:</strong> {log.spokeName || '—'}</p></div>
                        <div className="col-md-6"><p><strong>{t('ClassName')}:</strong> {log.className}</p></div>
                        <div className="col-md-6"><p><strong>{t('ServiceName')}:</strong> {log.serviceName || '—'}</p></div>
                        <div className="col-md-6"><p><strong>{t('ConsentId')}:</strong> {log.consentId}</p></div>
                        <div className="col-md-6"><p><strong>{t('SubjectId')}:</strong> {log.subjectId}</p></div>
                        <div className="col-md-6"><p><strong>{t('CorrelationId')}:</strong> {log.correlationId}</p></div>
                        <div className="col-md-6"><p><strong>{t('CallerIp')}:</strong> {log.callerIp || '—'}</p></div>
                        <div className="col-md-6"><p><strong>{t('RequestTimestamp')}:</strong> {fmt(log.requestTimestamp)}</p></div>
                        <div className="col-md-6"><p><strong>{t('ResponseTimestamp')}:</strong> {fmt(log.responseTimestamp)}</p></div>
                        <div className="col-md-6"><p><strong>{t('ResponseTimeMs')}:</strong> {log.responseTimeMs ?? '—'}</p></div>
                        <div className="col-md-6"><p><strong>{t('BackendStatusCode')}:</strong> {log.backendStatusCode ?? '—'}</p></div>
                        <div className="col-md-6"><p><strong>{t('GatewayStatusCode')}:</strong> {log.gatewayStatusCode ?? '—'}</p></div>
                        <div className="col-md-6"><p><strong>{t('ErrorCode')}:</strong> {log.errorCode || '—'}</p></div>
                        {log.errorMessage && (
                            <div className="col-12"><p><strong>{t('ErrorMessage')}:</strong> {log.errorMessage}</p></div>
                        )}
                    </div>
                </div>
                <div className="card-footer">
                    <button className="btn btn-secondary" onClick={() => navigate('/consumption-logs/list')}>
                        {t('Back')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConsumptionLogDetails;
